import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, Check, FileText, Crown, Lock, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useChat } from '../../contexts/ChatContext';
import { useAuth } from '../../contexts/AuthContext';
import Username, { getDisplayName } from '../Common/Username';
import GroupAvatar from '../Chat/GroupAvatar';
import { notesApi } from '../../services/cloudApi';

const NoteShareModal = ({ isOpen, onClose, note }) => {
  const { t } = useTranslation('notes');
  const { friends, groups } = useChat();
  const { authToken, isPlusUser } = useAuth();
  const [selectedFriends, setSelectedFriends] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [isSharing, setIsSharing] = useState(false);
  const [shareMode, setShareMode] = useState('friends');
  const [collabTooltip, setCollabTooltip] = useState(false);

  const availableFriends = useMemo(() =>
    friends.filter((f) => !f.isDeleted && f.lastMessageTime !== null),
    [friends]
  );

  const availableGroups = useMemo(() =>
    groups.filter((g) => g.is_active !== false),
    [groups]
  );

  const handleToggleFriend = (id) =>
    setSelectedFriends((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  const handleSelectGroup = (id) =>
    setSelectedGroup(selectedGroup === id ? null : id);

  const canShare = shareMode === 'friends' ? selectedFriends.length > 0 : !!selectedGroup;

  const handleShare = async () => {
    if (!note || !authToken || !canShare) return;
    setIsSharing(true);
    try {
      await notesApi.share({
        token: authToken, noteId: note.id,
        receiverIds: shareMode === 'friends' ? selectedFriends : [],
        groupId: shareMode === 'group' ? selectedGroup : null,
      });
      window.dispatchEvent(new CustomEvent('notesplus-show-toast', { detail: { type: 'success', title: t('common:success'), message: t('shareModal.shareSuccessMessage') } }));
      setSelectedFriends([]); setSelectedGroup(null); onClose();
    } catch {
      window.dispatchEvent(new CustomEvent('notesplus-show-toast', { detail: { type: 'error', title: t('common:errorTitle'), message: t('shareModal.shareErrorMessage') } }));
    } finally { setIsSharing(false); }
  };

  const handleBirlikteCalis = async () => {
    if (!note || !authToken || !isPlusUser || !canShare) return;
    setIsSharing(true);
    try {
      await notesApi.collabInvite({
        token: authToken, noteId: note.id,
        receiverIds: shareMode === 'friends' ? selectedFriends : [],
        groupId: shareMode === 'group' ? selectedGroup : null,
      });
      window.dispatchEvent(new CustomEvent('notesplus-show-toast', { detail: { type: 'success', title: t('common:success'), message: t('shareModal.collabSuccessMessage') } }));
      setSelectedFriends([]); setSelectedGroup(null); onClose();
    } catch (error) {
      const msg = error?.payload?.message || t('shareModal.collabErrorFallback');
      window.dispatchEvent(new CustomEvent('notesplus-show-toast', { detail: { type: 'error', title: t('common:errorTitle'), message: msg } }));
    } finally { setIsSharing(false); }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100000]"
            onClick={onClose}
          />

          <motion.div
            className="fixed inset-0 flex items-center justify-center z-[100001] p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.94, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.94, y: 8 }}
              transition={{ duration: 0.2, ease: [0.23, 1, 0.32, 1] }}
              className="w-full max-w-sm bg-[var(--color-bg-base)] rounded-2xl border border-white/[0.08] shadow-[0_32px_80px_rgba(0,0,0,0.7)] max-h-[85vh] flex flex-col overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-start justify-between px-5 pt-5 pb-4 flex-shrink-0">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#2563eb]/10 border border-[#3d8ef0]/20 flex items-center justify-center flex-shrink-0">
                    <Send className="w-4 h-4 text-[#3d8ef0]" strokeWidth={2} />
                  </div>
                  <h3 className="text-base font-semibold text-[var(--color-text-primary)]">{t('shareModal.title')}</h3>
                </div>
                <button onClick={onClose} className="p-1.5 rounded-lg text-[#4a6380] hover:text-[var(--color-text-secondary)] hover:bg-white/[0.06] transition-colors">
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Note Preview */}
              {note && (
                <div className="mx-5 mb-4 flex items-center gap-3 p-3 rounded-xl bg-[var(--color-bg-muted)] border border-white/[0.06] flex-shrink-0">
                  <div className="w-8 h-8 rounded-lg bg-[#2563eb]/10 flex items-center justify-center flex-shrink-0">
                    <FileText className="w-4 h-4 text-[#3d8ef0]" />
                  </div>
                  <p className="text-sm text-[var(--color-text-primary)] truncate font-medium">
                    {note.title || t('common:unnamed')}
                  </p>
                </div>
              )}

              {/* Mode Toggle */}
              <div className="px-5 mb-3 flex-shrink-0">
                <div className="flex gap-1.5 p-1 bg-[var(--color-bg-muted)] rounded-xl">
                  {['friends', 'group'].map((mode) => (
                    <button
                      key={mode}
                      onClick={() => { setShareMode(mode); mode === 'friends' ? setSelectedGroup(null) : setSelectedFriends([]); }}
                      className={`flex-1 py-2 rounded-lg text-xs font-semibold transition-all ${
                        shareMode === mode
                          ? 'bg-[#2563eb] text-white shadow-sm'
                          : 'text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)]'
                      }`}
                    >
                      {mode === 'friends' ? t('shareModal.modeFriends') : t('shareModal.modeGroup')}
                    </button>
                  ))}
                </div>
              </div>

              {/* List */}
              <div className="flex-1 overflow-y-auto px-5 pb-2">
                {shareMode === 'friends' ? (
                  availableFriends.length === 0 ? (
                    <div className="py-10 text-center text-sm text-[#4a6380]">{t('shareModal.noFriends')}</div>
                  ) : (
                    <div className="space-y-1">
                      {availableFriends.map((friend) => {
                        const id = friend.email || friend.id;
                        const isSelected = selectedFriends.includes(id);
                        return (
                          <button
                            key={id}
                            onClick={() => handleToggleFriend(id)}
                            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all ${
                              isSelected ? 'bg-[#2563eb]/10 border border-[#3d8ef0]/30' : 'hover:bg-[var(--color-bg-muted)] border border-transparent'
                            }`}
                          >
                            <div className="w-9 h-9 rounded-full bg-[#161f31] flex items-center justify-center text-sm font-semibold text-[var(--color-text-secondary)] flex-shrink-0 overflow-hidden">
                              {friend.avatarUrl
                                ? <img src={friend.avatarUrl} alt="" className="w-full h-full object-cover" />
                                : getDisplayName(friend).charAt(0).toUpperCase()}
                            </div>
                            <span className={`flex-1 text-left text-sm truncate ${isSelected ? 'text-[#3d8ef0]' : 'text-[var(--color-text-secondary)]'}`}>
                              <Username user={friend} variant="default" />
                            </span>
                            {!(friend.isPlus || friend.isPlusUser || friend.role === 'plus' || friend.role === 'admin') && (
                              <Lock className="w-3.5 h-3.5 text-[#4a6380] flex-shrink-0" />
                            )}
                            {isSelected && (
                              <div className="w-5 h-5 rounded-full bg-[#2563eb] flex items-center justify-center flex-shrink-0">
                                <Check className="w-3 h-3 text-white" />
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )
                ) : (
                  availableGroups.length === 0 ? (
                    <div className="py-10 text-center text-sm text-[#4a6380]">{t('shareModal.noGroups')}</div>
                  ) : (
                    <div className="space-y-1">
                      {availableGroups.map((group) => {
                        const isSelected = selectedGroup === group.id;
                        return (
                          <button
                            key={group.id}
                            onClick={() => handleSelectGroup(group.id)}
                            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all ${
                              isSelected ? 'bg-[#2563eb]/10 border border-[#3d8ef0]/30' : 'hover:bg-[var(--color-bg-muted)] border border-transparent'
                            }`}
                          >
                            <GroupAvatar group={group} size="sm" className="flex-shrink-0" />
                            <span className={`flex-1 text-left text-sm truncate ${isSelected ? 'text-[#3d8ef0]' : 'text-[var(--color-text-secondary)]'}`}>
                              {group.name}
                            </span>
                            {isSelected && (
                              <div className="w-5 h-5 rounded-full bg-[#2563eb] flex items-center justify-center flex-shrink-0">
                                <Check className="w-3 h-3 text-white" />
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )
                )}
              </div>

              {/* Footer */}
              <div className="px-5 py-4 border-t border-white/[0.07] flex items-center justify-between gap-2 flex-shrink-0">
                <div
                  className="relative"
                  onMouseEnter={() => !isPlusUser && setCollabTooltip(true)}
                  onMouseLeave={() => setCollabTooltip(false)}
                >
                  <button
                    onClick={isPlusUser ? handleBirlikteCalis : undefined}
                    disabled={!isPlusUser || isSharing || !canShare}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold transition-all ${
                      isPlusUser && canShare && !isSharing
                        ? 'bg-amber-500/10 border border-amber-500/30 text-amber-400 hover:bg-amber-500/20'
                        : 'text-[#4a6380] border border-white/[0.06] cursor-not-allowed'
                    }`}
                  >
                    <Crown className="w-3.5 h-3.5" />
                    {t('shareModal.collabButton')}
                  </button>
                  {collabTooltip && !isPlusUser && (
                    <div className="absolute bottom-full left-0 mb-2 px-2.5 py-1.5 bg-[var(--color-bg-base)] border border-white/[0.08] text-[var(--color-text-secondary)] text-xs rounded-lg whitespace-nowrap z-10 shadow-lg">
                      {t('shareModal.collabTooltip')}
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={onClose}
                    disabled={isSharing}
                    className="px-4 py-2 rounded-xl text-sm font-medium text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)] hover:bg-white/[0.05] transition-colors"
                  >
                    {t('common:cancel')}
                  </button>
                  <button
                    onClick={handleShare}
                    disabled={!canShare || isSharing}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold bg-[#2563eb] hover:bg-[#3d8ef0] text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {isSharing
                      ? <><div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />{t('shareModal.sharing')}</>
                      : <><Send className="w-3.5 h-3.5" />{t('shareModal.shareButton')}</>
                    }
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default NoteShareModal;
